import React, { memo } from 'react';
import { Calendar } from 'lucide-react';
import { formatDue, formatDay } from './taskDates';
import { cn } from '../../lib/utils';

/**
 * What a task is due, wherever a task is shown.
 *
 * Lives here rather than inside the board because the agent's headline
 * capability is scheduling, and the surfaces where a scheduling conversation
 * actually happens — the changeset, the panel's board tab — had no due date on
 * them at all. `grep due` returned zero hits in either. Asking the agent to push
 * everything to next week produced a panel that showed the words "due date" and
 * two raw ISO timestamps.
 *
 * The label is always derived from the stored date on every render, never from
 * the text that produced it. That is the point: the same task reads "Tomorrow",
 * then "Today", then "2 days late", with nobody editing anything.
 *
 * ── Clearing ────────────────────────────────────────────────────────────────
 * Clicking clears the date, wherever the badge is editable. That matters more
 * than it looks: text can only ever *set* a due date, never remove one, so
 * without this a task titled "Friday retro" is stuck with a date forever and the
 * parser's guesses are unappealable.
 */
function DueBadgeImpl({ due, onClear, size = 'default', muted, className, testId }) {
    const label = formatDue(due);
    if (!label) return null;

    const clearable = !!onClear;
    const exact = formatDay(due);

    return (
        <button
            type="button"
            disabled={!clearable}
            data-testid={testId}
            // Boards are drag surfaces. Without this the badge starts a drag
            // instead of taking the click.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.stopPropagation();
                if (clearable) onClear();
            }}
            title={clearable ? `Due ${exact} — click to clear` : `Due ${exact}`}
            aria-label={clearable ? `Due ${exact}, click to clear` : `Due ${exact}`}
            className={cn(
                'inline-flex flex-shrink-0 items-center gap-1 rounded-full border font-bold uppercase transition-all',
                size === 'sm' ? 'px-2 py-px text-[9px]' : 'px-2.5 py-0.5 text-[10px]',
                // A date on a parked task is inert by design — it is excluded
                // from every digest and every count until the task is promoted.
                // Rendering it in alarm-red would be the badge shouting about a
                // deadline nothing in the app is still tracking.
                muted
                    ? 'border-white/10 bg-white/5 text-muted-foreground'
                    : label.chip,
                clearable && 'cursor-pointer hover:opacity-80 hover:scale-105',
                className,
            )}
        >
            <Calendar className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-2.5 w-2.5'} />
            {label.label}
        </button>
    );
}

export const DueBadge = memo(DueBadgeImpl);
export default DueBadge;
